import { BG_CODE_FORMAT } from '~/models/bgCode'
import { formatDateValue, parseIsoDate } from '~/models/dateTime'
import type { SeriesKey } from '~/pages/BalanceDemand/constants'
import {
  DEFAULT_DETAIL_FILTER,
  type DetailFilterState,
} from '~/pages/BalanceDemandDetail/constants'
import {
  UNATTRIBUTED_BG_CODE,
  isIndividualBgCode,
} from '~/pages/BalanceDemandDetail/models/bgFilterOptions'

/** BG と小売の選択。小売は BG が個別 BG の時だけ意味を持つ。 */
export type DetailBgSelection = Pick<
  DetailFilterState,
  'bgCode' | 'retailerCode'
>

/** チャート系列トグル (子 11 + 親 3)。 */
export type DetailToggleState = Pick<
  DetailFilterState,
  'series' | 'voltageEnabled' | 'analysisEnabled' | 'detailEnabled'
>

/** URL クエリに載せる選択状態 (需要グループ・料金プランは載せない)。 */
export type DetailUrlState = DetailBgSelection &
  DetailToggleState & { displayDate: Date }

/** URL に指定が無い項目を埋める値 (保存値、または既定)。日付は含まない。 */
export type DetailUrlFallback = DetailBgSelection & DetailToggleState

const DATE_KEY = 'date'
const BG_CODE_KEY = 'bgCode'
const RETAILER_CODE_KEY = 'retailerCode'
const HIDE_KEY = 'hide'
const HIDE_GROUP_KEY = 'hideGroup'

/**
 * 子系列の公開 token。共有 URL・ブックマーク・保存値に載る文字列で、内部型
 * `SeriesKey` から導出しない。導出すると `SeriesKey` の改名で parse と serialize が
 * 一緒に変わり、既存の URL・保存値が黙って「全 ON」に落ちる。
 */
const SERIES_TOKENS: Readonly<Record<SeriesKey, string>> = {
  demandPlan: 'demandPlan',
  demandActual: 'demandActual',
  weather: 'weather',
  temperature: 'temperature',
  precipitation: 'precipitation',
  procurementNet: 'procurementNet',
  demandActualCorrected: 'demandActualCorrected',
  voltageExtraHigh: 'voltageExtraHigh',
  voltageHigh: 'voltageHigh',
  voltageLow: 'voltageLow',
  dewPoint: 'dewPoint',
}

type GroupKey = Exclude<keyof DetailToggleState, 'series'>

const GROUP_TOKENS: Readonly<Record<GroupKey, string>> = {
  voltageEnabled: 'voltage',
  analysisEnabled: 'analysis',
  detailEnabled: 'detail',
}

const SERIES_KEYS = Object.keys(SERIES_TOKENS) as SeriesKey[]
const GROUP_KEYS = Object.keys(GROUP_TOKENS) as GroupKey[]

const RETAILER_CODE_FORMAT = /^[0-9A-Z]{4}$/

export const DEFAULT_DETAIL_URL_FALLBACK: DetailUrlFallback = {
  bgCode: DEFAULT_DETAIL_FILTER.bgCode,
  retailerCode: DEFAULT_DETAIL_FILTER.retailerCode,
  series: DEFAULT_DETAIL_FILTER.series,
  voltageEnabled: DEFAULT_DETAIL_FILTER.voltageEnabled,
  analysisEnabled: DEFAULT_DETAIL_FILTER.analysisEnabled,
  detailEnabled: DEFAULT_DETAIL_FILTER.detailEnabled,
}

/**
 * request に載せてよい BG コード。backend は書式不正を InvalidArgument で返し、
 * 画面全体がエラーバナー (AC-BDB-033) になるので、URL・保存値は先にここで弾く。
 * 帰属不明 (`-UNK`) は選べない (表示日, エリア) でも backend が「すべて」で答える
 * ので通してよい (その後の補正で URL が書き戻される)。
 */
function isAcceptedBgCode(code: string): boolean {
  return (
    code === UNATTRIBUTED_BG_CODE ||
    (BG_CODE_FORMAT.test(code) && code.startsWith('L'))
  )
}

/**
 * request に載せてよい小売 (事業者) コード。backend `ParseBusinessCode` と同じ形に
 * 加え、帰属不明の予約 token (`-UNK`)。他の sentinel 形は backend が InvalidArgument。
 */
function isAcceptedRetailerCode(code: string): boolean {
  return code === UNATTRIBUTED_BG_CODE || RETAILER_CODE_FORMAT.test(code)
}

/**
 * BG と小売の組を検証する。不正な BG は「すべて」、BG が個別 BG でない時 (「すべて」・
 * 「帰属不明」) と不正な小売は「BG 全体」に落とす。BG が無いのに小売を残すと、後で BG を選んだ時に古い小売が
 * 黙って効く。
 */
export function normalizeBgSelection(
  bgCode: unknown,
  retailerCode: unknown,
): DetailBgSelection {
  const bg =
    typeof bgCode === 'string' && isAcceptedBgCode(bgCode) ? bgCode : ''
  const retailer =
    isIndividualBgCode(bg) &&
    typeof retailerCode === 'string' &&
    isAcceptedRetailerCode(retailerCode)
      ? retailerCode
      : ''
  return { bgCode: bg, retailerCode: retailer }
}

/** OFF の公開 token 列からトグル状態を組む。未知の token は無視する。 */
export function togglesFromHiddenTokens(
  hiddenSeries: readonly unknown[],
  hiddenGroups: readonly unknown[],
): DetailToggleState {
  const series = { ...DEFAULT_DETAIL_FILTER.series }
  for (const key of SERIES_KEYS) {
    if (hiddenSeries.includes(SERIES_TOKENS[key])) series[key] = false
  }
  const enabled = (key: GroupKey): boolean =>
    !hiddenGroups.includes(GROUP_TOKENS[key])
  return {
    series,
    voltageEnabled: enabled('voltageEnabled'),
    analysisEnabled: enabled('analysisEnabled'),
    detailEnabled: enabled('detailEnabled'),
  }
}

/** トグル状態を OFF の公開 token 列にする (並びは固定)。 */
export function hiddenTokensOf(toggles: DetailToggleState): {
  hiddenSeries: string[]
  hiddenGroups: string[]
} {
  return {
    hiddenSeries: SERIES_KEYS.filter((key) => !toggles.series[key]).map(
      (key) => SERIES_TOKENS[key],
    ),
    hiddenGroups: GROUP_KEYS.filter((key) => !toggles[key]).map(
      (key) => GROUP_TOKENS[key],
    ),
  }
}

/**
 * `YYYY-MM-DD` を表示日にする。存在しない暦日 ("2026-02-31") と未来日
 * (AC-BDB-004a で選べない) は当日に落とす。
 */
function parseDisplayDate(raw: string | null, today: Date): Date {
  if (raw == null) return today
  // parseIsoDate は存在しない暦日を繰り上げて受理するので往復一致で弾く。
  const parsed = parseIsoDate(raw)
  if (parsed === undefined || formatDateValue(parsed) !== raw) return today
  return parsed.getTime() > today.getTime() ? today : parsed
}

function splitTokens(raw: string | null): string[] {
  return raw == null ? [] : raw.split(',')
}

/**
 * URL クエリから選択状態を解決する。
 *
 * `date` を持つ URL はこの画面が書いた正準形なので、省略された key は既定値の
 * 明示として読む。`date` を持たない URL (ダッシュボードからの導線・手打ち) だけ、
 * 省略された key を `fallback` (保存値) で埋める。正準形の省略まで保存値で
 * 埋めると、「すべて」・全 ON で共有した URL が受け手の保存値で別の状態に開く。
 *
 * BG と小売は組で解決する。`bgCode` key が無い時は URL の `retailerCode` を
 * 使わない (BG の無い小売は意味を持たない)。
 */
export function resolveDetailUrlState(
  params: URLSearchParams,
  today: Date,
  fallback: DetailUrlFallback,
): DetailUrlState {
  const base = params.has(DATE_KEY) ? DEFAULT_DETAIL_URL_FALLBACK : fallback

  const bgSelection = params.has(BG_CODE_KEY)
    ? normalizeBgSelection(
        params.get(BG_CODE_KEY),
        params.get(RETAILER_CODE_KEY),
      )
    : { bgCode: base.bgCode, retailerCode: base.retailerCode }

  const fromUrl = togglesFromHiddenTokens(
    splitTokens(params.get(HIDE_KEY)),
    splitTokens(params.get(HIDE_GROUP_KEY)),
  )
  const groupSource = params.has(HIDE_GROUP_KEY) ? fromUrl : base

  return {
    displayDate: parseDisplayDate(params.get(DATE_KEY), today),
    ...bgSelection,
    series: params.has(HIDE_KEY) ? fromUrl.series : base.series,
    voltageEnabled: groupSource.voltageEnabled,
    analysisEnabled: groupSource.analysisEnabled,
    detailEnabled: groupSource.detailEnabled,
  }
}

function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string,
): void {
  if (value === '') {
    params.delete(key)
  } else {
    params.set(key, value)
  }
}

/**
 * 選択状態を URL クエリに書く。`date` は常に書き (正準形の印)、既定値 (すべて /
 * BG 全体 / 全 ON) の key は省く。この画面が扱わない key (`areaCode` /
 * `caseId` 等) はそのまま残す。
 */
export function writeDetailUrlState(
  prev: URLSearchParams,
  state: DetailUrlState,
): URLSearchParams {
  const next = new URLSearchParams(prev)
  next.set(DATE_KEY, formatDateValue(state.displayDate))
  setOrDelete(next, BG_CODE_KEY, state.bgCode)
  setOrDelete(
    next,
    RETAILER_CODE_KEY,
    state.bgCode === '' ? '' : state.retailerCode,
  )
  const { hiddenSeries, hiddenGroups } = hiddenTokensOf(state)
  setOrDelete(next, HIDE_KEY, hiddenSeries.join(','))
  setOrDelete(next, HIDE_GROUP_KEY, hiddenGroups.join(','))
  return next
}
